import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { Avatar } from '@/components/Avatar';
import { Modal } from '@/components/Modal';
import { NewBadge } from '@/components/NewBadge';
import {
  forgetCredentials,
  hasSavedCredentials,
  recallCredentials,
  rememberCredentials,
} from '@/lib/auth/credentialVault';
import {
  login,
  logout,
  useSession,
  VENDOR_KIND_LABEL,
  VENDOR_KINDS,
  type VendorKind,
} from '@/lib/auth/session';
import { newRfqCount, newsForKind } from '@/lib/gatewayNews';
import { RFQ_SPACE_ROOT } from '@/lib/menu';
import { useRfqs } from '@/lib/queries';
import { IN_SAFE_APP, SAFE_APP_TITLE } from '@/lib/safeApp';

import { InquiryForm } from './login/InquiryForm';
import { PasswordResetForm } from './login/PasswordResetForm';
import { RegisterForm } from './login/RegisterForm';
import { SiteTodoProbe } from './login/SiteTodoProbe';

/*
  보미건설 파트너 페이지 — 로그인 앞의 첫 화면(DOC-96 §9 · 사용자 그림 2026-09-29).

  왼쪽에 갈래 칸 셋(외주·구매·용역), 오른쪽에 로그인 · 협력업체 등록 · 문의하기, 아래에 본사 홈페이지와
  개인정보 처리방침. 갈래는 **업체의 속성**이고 칸 셋은 **로그인 뒤 가는 곳**이다(사용자 결정 2026-09-29) —
  로그인하면 서버가 세션에 갈래를 실어 주고 메뉴·홈이 그것으로 걸러진다(`lib/menu.ts`). 그래서 로그인 전의
  큰 칸은 문이 아니다: 누르면 로그인 칸으로 초점만 간다. 등록 신청은 오른쪽 「협력업체 등록」 하나뿐이다
  (사용자 지시 2026-09-29 「등록 신청은 하나로」).

  로그인 문은 하나다(사용자 지시 2026-09-29 「협력사·감리·직원 나누지 말고 계정에 따라 분리」). 협력사·감리·
  직원을 화면이 가르지 않고 서버가 계정을 보고 가른다 — 외부 계정 먼저, 안 맞으면 직원(`loginAny`). 직원은
  front 와 같은 이메일·비밀번호다(DOC-87 결정 ⑤). DOC-87 §3-8 의 두 칸은 걷었다.

  등록 신청과 문의는 이 화면 위에 모달로 뜬다 — 첫 화면을 떠나지 않는다.
*/
const HOME_URL = 'https://www.bomienc.com';
const PRIVACY_URL = '/privacy.html';

/*
  갈래 칸의 대표 그림 — 사용자가 `docs/design/vendor/` 로 가져온 시안(2026-09-29).

  셋 다 `viewBox="0 0 600 800"`(3:4 세로)이고 하늘 `#E6EDF1` · 지면 `#C8B597` 로 같은 팔레트다.
  PNG 도 함께 왔지만 <b>SVG 를 쓴다</b> — 칸이 화면 크기를 따라 늘어나는 자리라 래스터는 흐려진다.
  원본은 그대로 두고 여기로 복사했다(`vendor/public/brand/`): 시안 폴더는 빌드에 실리지 않는다.
*/
const KIND_ART: Record<VendorKind, string> = {
  SUBCONTRACT: '/brand/kind-subcontract.svg',
  PURCHASE: '/brand/kind-purchase.svg',
  SERVICE: '/brand/kind-service.svg',
};

/** 그림의 하늘색. 칸이 그림보다 길 때 그 여백을 이 색으로 메워야 이어 붙은 것처럼 보인다. */
const ART_SKY = '#E6EDF1';

type Sheet = 'register' | 'inquiry' | 'reset' | null;

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /*
    로그인했는가 — <b>세션으로 본다</b>. 토큰만 보면 죽은 토큰이 남아 있는 사람에게 관문이 서고,
    칸을 눌러 들어간 뒤 문지기가 다시 여기로 돌려보낸다(그 왕복이 「로그인이 안 된다」로 읽힌다).
  */
  const session = useSession();
  const me = session.data ?? null;

  /*
    관문 칸의 「New N」(사용자 지시 2026-11-04). 배정된 현장마다 사이드바 배지와 같은 수를 세어 모은다
    ({@link SiteTodoProbe}). 견적·입찰은 현장을 가리지 않는 목록 하나다.
  */
  const [siteCounts, setSiteCounts] = useState<Record<string, Record<string, number>>>({});
  const onSiteCounts = useCallback((siteId: string, counts: Record<string, number>) => {
    setSiteCounts((prev) => ({ ...prev, [siteId]: counts }));
  }, []);
  const rfqs = useRfqs({ enabled: me !== null && me.rfqEnabled && !IN_SAFE_APP });
  const perSite = me === null ? [] : me.sites.map((site) => siteCounts[site.id] ?? {});
  const newRfqs = newRfqCount(rfqs.data ?? []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  /** 접수·문의가 끝난 뒤 로그인 칸 위에 한 줄. 다른 것을 누르면 사라진다. */
  const [notice, setNotice] = useState<string | null>(null);
  /** 큰 칸을 누르면 여기로 초점이 온다. */
  const emailRef = useRef<HTMLInputElement>(null);

  /*
    체크 상태를 「저장된 것이 있는가」로 시작한다. 저장해 둔 사람에게는 켜진 채로 보이고,
    끄면 그 자리에서 지워진다 — 켠 적 없는 사람에게 기본으로 켜 두지 않는다.
    이 앱은 협력사 담당자의 **개인 기기**에서도 도는 것이라 더 그렇다.
  */
  const [remember, setRemember] = useState(hasSavedCredentials);

  // 봉인을 여는 일은 비동기다(IndexedDB + WebCrypto). 화면이 먼저 뜨고 값이 나중에 채워진다.
  useEffect(() => {
    let alive = true;
    void recallCredentials().then((saved) => {
      if (!alive || saved === null) return;
      setEmail(saved.email);
      setPassword(saved.password);
    });
    return () => {
      alive = false;
    };
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setFailed(false);
    setNotice(null);
    const trimmed = email.trim();
    try {
      await login(trimmed, password);

      /*
        **성공한 뒤에만** 저장한다. 틀린 비밀번호를 기억해 두면 다음에 열 때도 그대로 실패하고,
        사용자는 「저장 기능이 고장났다」로 읽는다.
      */
      if (remember) {
        await rememberCredentials({ email: trimmed, password });
      }
      else {
        forgetCredentials();
      }

      /*
        캐시를 비운다 — 앞사람의 현장·공고가 남아 있으면 안 된다. 세션은 곧바로 다시 읽힌다
        ({@code useSession} 이 토큰을 보고 켜진다).

        ★ <b>여기서 화면을 옮기지 않는다</b>(사용자 지시 2026-09-30). 로그인은 이 화면을
          <b>관문으로 바꾸는 일</b>이고, 어디로 갈지는 사람이 칸을 눌러 정한다. 예전에는 곧바로
          홈으로 밀어 넣었고, 그래서 견적만 보러 온 사람도 현장 대시보드를 지나야 했다.
      */
      queryClient.clear();
    }
    catch {
      /*
        사유를 화면에 쓰지 않는다. 「없는 계정」과 「비밀번호가 틀림」을 갈라 보여 주면
        그 자체가 계정이 있는지 확인해 주는 통로가 된다. 자세한 사유는 서버 로그에 남는다.
        서버가 두 문을 다 두드려도 답은 이 한 문장이다.
      */
      setFailed(true);
      // 비밀번호가 바뀌었을 수 있다. 저장해 둔 값이 계속 실패를 되풀이하지 않게 지운다.
      forgetCredentials();
    }
    finally {
      setPending(false);
    }
  }

  function onToggleRemember(checked: boolean) {
    setRemember(checked);
    // 끄는 순간 지운다 — 다음 로그인까지 남겨 두면 「껐는데 아직 있다」가 된다.
    if (!checked) forgetCredentials();
  }

  /**
   * 로그아웃 — <b>관문에 머문다</b>. 다른 화면으로 보내지 않는다: 지금 서 있는 이 화면이 로그인
   * 카드로 되돌아가는 것이 곧 로그아웃한 모습이다.
   *
   * <p>저장해 둔 아이디·비밀번호는 <b>지우지 않는다</b>. 로그아웃은 「이 기기를 그만 쓴다」가 아니라
   * 「지금 계정에서 나간다」다 — 지우는 손은 「로그인 정보 저장」 체크를 끄는 것이다.
   */
  async function onLogout() {
    await logout();
    queryClient.clear();
    setNotice(null);
    setPassword('');
  }

  /**
   * 이 갈래 칸이 <b>우리 문</b>인가 (사용자 확정 2026-09-30).
   *
   * <ul>
   *   <li>로그인 전 — 셋 다 그대로다. 아직 누구인지 모르고, 이 화면의 첫 일은 <b>소개</b>다.
   *   <li>협력사·<b>감리</b>·발주처 — <b>부여받은 갈래만</b>. 하나도 없으면 셋 다 흑백이다
   *       (사용자 지시 2026-09-30 「지정이 안돼있으면 다 안돼야지」·「감리, 직원도 … 협력사
   *       마스터에서 전부 관리」). 셋 다 협력사 마스터에 행이 있어 갈래를 줄 자리가 있다.
   *   <li><b>직원도 같다</b>(V369). 담는 자리만 다르고(`portal_staff_kinds`) 주는 화면은 같다 —
   *       협력사 마스터 › 직원 접속. 예외가 하나도 없다.
   * </ul>
   */
  function isMyKind(kind: VendorKind): boolean {
    if (me === null) return true;
    return me.vendorKinds.includes(kind);
  }

  /**
   * 견적·입찰 칸이 열리는가 — <b>업체에 부여된 권한</b>이다(V364 · 사용자 지시 2026-09-30).
   *
   * <p>로그인 전에는 열린 모습이다(누르면 로그인 칸으로 초점). 로그인한 뒤에는 협력사 마스터에서
   * 켜 준 업체만 들어간다 — 꺼져 있으면 흑백이고, 주소로 들어가도 서버가 사람 말로 막는다
   * ({@code ExternalRfqService.requireVendor}).
   */
  const rfqOpen = me === null || me.rfqEnabled;

  /*
    안전관리 앱(DOC-140)에서는 관문에 머물지 않는다 — 고를 문(갈래 칸 · 견적)이 없으니 로그인한 순간 홈(= 첫 안전
    화면)으로 간다. 웹 포털의 「여기서 화면을 옮기지 않는다」(위 onSubmit)는 그대로다.
  */
  useEffect(() => {
    if (IN_SAFE_APP && me !== null) navigate('/', { replace: true });
  }, [me, navigate]);

  /** 관문의 문 — 누르면 그 공간으로 들어간다. 로그인 전에는 로그인 칸으로 초점만 간다. */
  function enter(to: string) {
    if (me === null) {
      emailRef.current?.focus();
      return;
    }
    navigate(to);
  }

  /*
    큰 화면(사용자 지시 2026-09-29 「화면 큰 곳에서는 반응형이 안 되네」): 폭은 120rem 까지 따라간다.
    높이는 <b>머리 위·꼬리 아래에 남는 만큼을 본문이 가운데로 갖는다</b>(「height 정렬은 가운데로」) —
    머리는 맨 위, 꼬리는 맨 아래에 그대로 두고 그 사이만 센터링한다.
  */
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[120rem] flex-col p-4 sm:p-6 lg:p-8 2xl:p-10">
      {me !== null && !IN_SAFE_APP && me.sites.map((site) => (
        <SiteTodoProbe key={site.id} siteId={site.id} partyType={me.partyType} onCounts={onSiteCounts} />
      ))}
      <header className="mb-6 flex items-center justify-center gap-3">
        <img src="/brand/bomi-ci.png" alt="BoMI" className="h-8 w-auto" />
        <h1 className="text-ui-lg font-semibold">{IN_SAFE_APP ? SAFE_APP_TITLE : '보미건설 파트너 페이지'}</h1>
      </header>

      <main className="flex flex-1 items-center">
      {/*
        칸의 키는 <b>오른쪽 기둥(로그인 ~ 문의하기)이 정한다</b>(사용자 지시 2026-09-29 「옆에 사이즈랑 일치」).
        창 높이에 맞춰 늘리지는 않는다 — 늘리면 그림보다 훨씬 길어져 하늘 여백만 커진다(1920 에서 절반이
        빈 하늘이었다). 기둥의 제 높이가 곧 줄 높이이고, 칸 셋이 그 키를 그대로 받는다.
        칸을 키우는 것은 <b>폭</b>이다 — 기둥을 20rem 으로 고정하고 나머지를 칸 셋이 다 갖는다
        (사용자 지시 「너무 작지 않나」).
      */}
      <div
        className={IN_SAFE_APP
          ? 'mx-auto grid w-full max-w-sm content-start gap-4'
          : 'grid w-full content-start items-start gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-stretch lg:gap-6 2xl:gap-8'}
      >
        {/*
          ── 갈래 칸 셋 ──
          그림이 위, 이름표가 아래인 카드다.

          ★ 그림은 칸을 가장자리까지 채우고(`object-cover`) <b>아래를 기준으로 맞춘다</b>(`object-bottom`).
            칸이 시안의 3:4 보다 넓어지면 남는 것은 <b>위쪽 하늘</b>이라 그쪽만 잘려 나간다 — 크레인·트럭·
            측량기는 그대로 남는다. 세로 화면(lg 미만)에서는 3:4 그대로라 아무것도 잘리지 않는다.
        */}
        {/* 안전관리 앱에는 갈래 칸이 없다 — 칸 셋은 포털의 업무 공간으로 가는 문이다. */}
        {!IN_SAFE_APP && (
        <section aria-label="갈래">
          <div className="grid content-start justify-items-center gap-3 sm:grid-cols-3 lg:h-full lg:content-stretch lg:gap-4 2xl:gap-6">
            {VENDOR_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className={`card group relative flex w-full flex-col overflow-hidden p-0 text-left transition-shadow lg:h-full lg:max-w-[21rem] ${
                  isMyKind(kind) ? 'hover:shadow-md' : 'cursor-default opacity-40 grayscale'
                }`}
                /*
                  로그인 <b>뒤에는 문이다</b>(사용자 지시 2026-09-30 「버튼 타고 들어가도록」).
                  전에는 로그인 칸으로 초점만 갔다 — 그때는 갈 곳이 없으니 그것이 맞았다.

                  ★ <b>내 갈래 칸만 열린다</b>(사용자 확정 2026-09-30). 갈래는 업체의 속성이라
                    고르는 것이 아닌데 칸이 셋이면, 구매 업체 담당자가 「외주」를 눌러도 구매 화면이
                    떠서 <b>칸이 거짓말을 한다</b>. 나머지 둘은 흐리게 남긴다 — 지우지 않는 이유는
                    「우리는 어느 갈래로 등록돼 있나」가 그 대비로 읽히기 때문이다.

                  ★ 셋 다 <b>같은 주소</b>다. 갈래마다 화면을 따로 만들 필요가 없다 — 도착한 뒤
                    사이드바와 홈 위젯이 우리 갈래대로 걸러진다(`menuFor` · `widgetsFor`).
                    구매 업체는 8개, 용역 14개, 외주 19개를 본다(실측 2026-09-30).
                */
                onClick={() => enter('/')}
                disabled={!isMyKind(kind)}
              >
                {me !== null && isMyKind(kind) && <NewBadge n={newsForKind(me.partyType, kind, perSite)} />}
                {/*
                  그림 자리 — 세로는 3:4 그대로, 가로는 이름표를 뺀 나머지 키를 다 갖는다.
                  ★ 칸 폭에 <b>상한(21rem)</b>을 둔다. 폭이 넓어질수록 3:4 그림에서 잘려 나가는 하늘이 두꺼워져
                    크레인 지브가 사라진다(1920 에서 실측). 상한을 두면 칸 비율이 3:4 근처에 머물러 거의 안 잘린다.
                */}
                <div
                  className="relative aspect-[3/4] w-full lg:aspect-auto lg:min-h-0 lg:flex-1"
                  style={{ background: ART_SKY }}
                >
                  <img
                    src={KIND_ART[kind]}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover object-bottom"
                  />
                </div>

                {/* 이름표. 그림 위에 얹지 않고 아래 칸으로 나눈다 — 선이 가는 그림이라 덮으면 탁해진다. */}
                <span
                  className="flex items-center justify-between gap-2 border-t px-5 py-4 text-xl font-semibold 2xl:px-6 2xl:py-5 2xl:text-2xl"
                  style={{ borderColor: 'var(--border)' }}
                >
                  {VENDOR_KIND_LABEL[kind]}
                  <span
                    className="text-ui-md transition-transform group-hover:translate-x-1"
                    style={{ color: 'var(--accent)' }}
                    aria-hidden
                  >
                    →
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
        )}

        {/* ── 오른쪽 기둥 — 로그인 카드는 내용만큼, 등록·문의는 창 높이의 10% 로 묶는다. ── */}
        <aside className="flex flex-col gap-3 lg:gap-4 2xl:gap-6">
          {/*
            접수·문의 알림은 <b>두 카드 바깥</b>이다. 카드 안에 두면 로그인 전후로 한쪽에만 자리가
            생겨 아래 칸들이 밀린다.
          */}
          {notice !== null && (
            <p className="text-ui-xs" style={{ color: 'var(--accent)' }}>
              {notice}
            </p>
          )}

          {/*
            로그인하면 <b>이 카드만 바뀐다</b>(사용자 지시 2026-09-30) — 아이디·비밀번호 칸 자리에
            누구로 들어와 있는지와 로그아웃이 선다. 화면 전체를 갈아 끼우지 않는 이유: 왼쪽 칸 셋과
            아래 등록·문의는 로그인 전후로 같은 자리에 있어야 한다. 카드 하나만 바뀌면 사람은
            「내가 로그인됐다」를 <b>제자리에서</b> 읽는다.

            ★ 두 카드를 <b>한 격자 칸에 겹친다</b>(사용자 지시 2026-09-30 「로그인해서 컴포넌트가
              줄어들면 안돼」). 둘 다 자리를 차지하므로 칸의 키는 <b>큰 쪽</b>(로그인 폼)으로 고정되고,
              로그인해도 기둥이 안 줄어든다. 기둥이 줄면 <b>왼쪽 갈래 칸 셋까지 따라 줄어든다</b> —
              칸의 키를 이 기둥이 정하기 때문이다(아래 격자 주석).

              숨기는 쪽은 `invisible` 이다(`hidden` 이 아니다): 자리를 남겨야 키가 유지된다.
              `visibility:hidden` 은 탭 포커스도 받지 않아 안 보이는 폼에 커서가 들어가지 않는다.
          */}
          <div className="grid">
            {/*
              세로로 편다. 이 카드는 로그인 폼만큼의 키를 받으므로(위 ★) 내용을 위에 몰면 아래가
              빈 상자로 남는다 — 로그아웃을 <b>바닥에</b> 붙여 카드를 채운다.
            */}
            <section
              className={`card flex flex-col p-4 [grid-area:1/1] ${me === null ? 'invisible' : ''}`}
              aria-hidden={me === null}
            >
              {/*
                프로필 칸을 누르면 마이페이지다(사용자 지시 2026-09-30). 사진과 보직을 거기서 고친다.

                동그라미는 <b>사진이 있으면 얼굴, 없으면 이름 첫 글자</b>다({@link Avatar}) —
                직원은 인사자료의 증명사진이 그대로 온다. 회색 사람 아이콘은 두지 않는다:
                「사진이 빠진 칸」으로 읽혀 사람이 자기 화면을 고장으로 본다.

                로그인 전에도 이 카드는 자리를 지킨다(`invisible` — 안 보일 뿐이다). 그때는 채울
                이름이 없고, 사진 질의도 토큰이 없어 나가지 않는다.
              */}
              <Link to="/mypage" className="flex items-center gap-10 my-auto mx-auto">
                <Avatar size={64} name={me?.name} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ui-md font-semibold">{me?.name}님, 환영합니다.</span>
                  <span className="block truncate text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
                    {me?.partyName}
                    {/* 맡는 갈래를 함께 적는다 - 여럿일 수 있다(V364). 미지정이면 이 꼬리가 없다. */}
                    {me !== null && me.vendorKinds.length > 0
                      && ` · ${VENDOR_KINDS.filter((kind) => me.vendorKinds.includes(kind))
                        .map((kind) => VENDOR_KIND_LABEL[kind]).join('·')}`}
                  </span>
                </span>
              </Link>

              <button
                type="button"
                className="btn-secondary mt-auto w-full"
                disabled={me === null}
                onClick={() => void onLogout()}
              >
                로그아웃
              </button>
            </section>

            <form
              onSubmit={onSubmit}
              className={`card p-4 [grid-area:1/1] ${me !== null ? 'invisible' : ''}`}
              aria-hidden={me !== null}
            >
            <label className="mb-3 block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
                아이디
              </span>
              <input
                ref={emailRef}
                type="email"
                className="input-field"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                inputMode="email"
                placeholder="이메일"
                required
              />
            </label>

            <label className="mb-3 block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
                비밀번호
              </span>
              <input
                type="password"
                className="input-field"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>

            {/* 손가락으로 누르는 화면이라 라벨까지 눌리게 label 로 감싼다. */}
            <label className="mb-3 flex cursor-pointer items-center gap-2 text-ui-sm">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => onToggleRemember(event.target.checked)}
              />
              <span style={{ color: 'var(--text-secondary)' }}>로그인 정보 저장</span>
            </label>

            {failed && (
              <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>
                로그인하지 못했습니다. 이메일과 비밀번호를 확인해 주세요.
              </p>
            )}

            <button type="submit" className="btn-primary w-full" disabled={pending || me !== null}>
              {pending ? '확인 중입니다…' : '로그인'}
            </button>
            <button
              type="button"
              className="mt-2 w-full text-center text-ui-xs"
              style={{ color: 'var(--text-secondary)' }}
              onClick={() => {
                setNotice(null);
                setSheet('reset');
              }}
            >
              비밀번호 찾기
            </button>
            </form>
          </div>

          {/*
            ── 견적·입찰 문 ──
            왼쪽 칸 셋과 <b>다른 축</b>이라 저기 넷째 칸으로 끼우지 않았다: 셋은 업체의 갈래이고
            이것은 일의 종류다. 넷째로 세우면 「우리 업체는 외주인데 견적이라는 갈래도 있나」가 된다.

            로그인 전에도 서 있다 — 누르면 로그인 칸으로 초점이 간다(칸 셋과 같은 규칙).
            감추지 않는 이유: 이 페이지에 견적 자리가 있다는 것부터 알려야 한다.
          */}
          {/* 견적 · 등록 · 문의는 포털의 일이다 — 안전관리 앱에는 로그인 카드만 선다. */}
          {!IN_SAFE_APP && (
          <>
          <button
            type="button"
            className={`card relative flex min-h-24 items-center justify-between p-4 text-left transition-shadow lg:h-[10vh] lg:min-h-0 lg:shrink-0 2xl:p-6 ${
              rfqOpen ? 'hover:shadow-md' : 'cursor-default opacity-40 grayscale'
            }`}
            disabled={!rfqOpen}
            onClick={() => {
              setNotice(null);
              enter(RFQ_SPACE_ROOT);
            }}
          >
            {me !== null && rfqOpen && <NewBadge n={newRfqs} />}
            <span className="text-ui-lg font-semibold 2xl:text-xl">견적·입찰</span>
            <span style={{ color: 'var(--accent)' }}>→</span>
          </button>

          <button
            type="button"
            // 가로에서는 창 높이의 10% 로 묶는다(사용자 지시 2026-09-29) — 남는 키를 나눠 가지면 빈 상자가 된다.
            className="card flex min-h-24 items-center justify-between p-4 text-left lg:h-[10vh] lg:min-h-0 lg:shrink-0 2xl:p-6"
            onClick={() => {
              setNotice(null);
              setSheet('register');
            }}
          >
            <span className="text-ui-lg font-semibold 2xl:text-xl">협력업체 등록</span>
            <span style={{ color: 'var(--accent)' }}>→</span>
          </button>

          <button
            type="button"
            // 가로에서는 창 높이의 10% 로 묶는다(사용자 지시 2026-09-29) — 남는 키를 나눠 가지면 빈 상자가 된다.
            className="card flex min-h-24 items-center justify-between p-4 text-left lg:h-[10vh] lg:min-h-0 lg:shrink-0 2xl:p-6"
            onClick={() => {
              setNotice(null);
              setSheet('inquiry');
            }}
          >
            <span className="text-ui-lg font-semibold 2xl:text-xl">문의하기</span>
            <span style={{ color: 'var(--accent)' }}>→</span>
          </button>
          </>
          )}
        </aside>
      </div>
      </main>

      <footer className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
        <a href={HOME_URL} target="_blank" rel="noopener noreferrer">
          보미건설 홈페이지
        </a>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          개인정보 처리방침
        </a>
      </footer>

      {/* 등록 신청은 창까지 그 파일이 그린다 — 「접수」가 제목 줄의 「닫기」 옆에 서야 하는데, 눌릴 수 있는지를 아는 것이 그 폼이다. */}
      {sheet === 'register' && (
        <RegisterForm
          onClose={() => setSheet(null)}
          onDone={() => {
            setSheet(null);
            setNotice('접수되었습니다. 검토 후 담당자 이메일로 안내드립니다.');
          }}
        />
      )}

      {sheet === 'reset' && (
        <PasswordResetForm
          initialEmail={email}
          onClose={() => setSheet(null)}
          onDone={() => {
            setSheet(null);
            setPassword('');
            setNotice('비밀번호를 변경했습니다. 새 비밀번호로 로그인해 주세요.');
          }}
        />
      )}

      {sheet === 'inquiry' && (
        <Modal
          title="문의하기"
          onClose={() => setSheet(null)}
          maxWidth="32rem"
          actions={
            <button type="button" className="btn-secondary" onClick={() => setSheet(null)}>
              닫기
            </button>
          }
        >
          <InquiryForm
            onDone={() => {
              setSheet(null);
              setNotice('문의를 보냈습니다. 적어 주신 이메일로 답변드립니다.');
            }}
          />
        </Modal>
      )}
    </div>
  );
}
